/* eslint-disable @typescript-eslint/no-explicit-any */
import { map, filter, pluck } from 'ramda';
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import styled, { ThemeProvider } from 'styled-components';

import FullscreenOnlyLayout from './layouts/fullscreenOnlyLayout.tsx';
import MapOnlyLayout from './layouts/mapOnlyLayout.tsx';
import ModEditorLayout from './layouts/modEditorLayout.tsx';
import ModLayout from './layouts/modLayout.tsx';
import SearchDirectoryLayout from './layouts/searchDirectoryLayout.tsx';
import StandardLayout from './layouts/standardLayout.tsx';
import SystemErrorBoundary from './SystemErrorBoundary.tsx';
import type { MessageBus } from './types/bus.ts';

interface LayerWidget {
  id: string;
  show: boolean;
  isOverlay: boolean;
  layoutId?: string;
  layoutName?: string;
  widgetType?: string;
  widget: React.ComponentType<any>;
  order?: number | null;
  shouldShow?: (currentlyShowing: string[]) => boolean;
  [key: string]: unknown;
}

let appRoot: Root | null = null;

const layouts: Record<string, React.ComponentType<any>> = {
  standard: StandardLayout,
  searchDirectory: SearchDirectoryLayout,
  fullscreen: FullscreenOnlyLayout,
  mapOnly: MapOnlyLayout,
  modEditor: ModEditorLayout,
  mod: ModLayout,
};

// sort by the order property.
const orderComparator = (w1: LayerWidget, w2: LayerWidget) => {
  if (w1.order == null) {
    // null or undefined
    return w2.order == null ? 0 : 1;
  } // any order is higher pri than none
  if (w2.order == null) {
    return -1;
  }
  return w1.order - w2.order;
};

const AppMain = styled.div`
  height: 100%;
`;

interface LayerManagerProps {
  app: any;
  layers: Record<string, LayerWidget>;
  currentLayout: string;
  mapLoaded: boolean;
  Loading: React.ComponentType<any>;
}

const LayerManager = ({ app, layers, currentLayout, mapLoaded, Loading }: LayerManagerProps) => {
  const layerWidgets = Object.values(layers)
    .sort(orderComparator)
    .map($widget =>
      $widget.show ? (
        <$widget.widget key={`widget-${$widget.id}`} layoutId={$widget.layoutId} widgetType={$widget.widgetType} />
      ) : null,
    );
  const Layout = layouts[currentLayout];
  const content = (
    <Layout app={app} uiHide={app.config.uiHide || {}} Loading={Loading} mapLoaded={mapLoaded}>
      {layerWidgets}
    </Layout>
  );

  const $login = layers.login;
  if ($login && $login.show) {
    return <$login.widget key={`widget-login`} />;
  }

  return content;
};

const initLayerManager = async (app: any) => {
  const config = app.config;
  const log = app.log.sublog('layerManager');
  const { getTheme, GlobalStyle, Loading, getThemeState } = app.themePack;
  const renderDiv = config.renderDiv || '#renderDiv';
  const renderElement = document.querySelector(renderDiv);
  if (!renderElement) {
    throw Error(`DOM element not found matching selector: ${renderDiv}`);
  }

  // State lives here, outside React
  const state: {
    layers: Record<string, LayerWidget>;
    currentLayout: string;
    mapLoaded: boolean;
    previousLayer: string;
  } = {
    layers: {},
    currentLayout: config.initialLayout || 'standard',
    mapLoaded: false,
    previousLayer: '',
  };

  appRoot = createRoot(renderElement);

  const renderAppRoot = () => {
    appRoot!.render(
      <SystemErrorBoundary app={app}>
        <ThemeProvider theme={getTheme()}>
          <AppMain className="atrius-map">
            <GlobalStyle />
            <LayerManager
              app={app}
              layers={state.layers}
              currentLayout={state.currentLayout}
              mapLoaded={state.mapLoaded}
              Loading={Loading}
            />
          </AppMain>
        </ThemeProvider>
      </SystemErrorBoundary>,
    );
  };

  const getShowingLayersArray = () => Object.keys(filter((layer: LayerWidget) => layer.show === true, state.layers));

  const getVisibleLayerByLayoutId = (layoutId: string) =>
    Object.keys(filter((layer: LayerWidget) => layer.show === true && layer.layoutId === layoutId, state.layers));

  // Before updating layers, check for any shouldShow triggers...
  const updateLayers = (layers: Record<string, LayerWidget>) => {
    let dirty = true;
    while (dirty) {
      const currentlyShowing = getShowingLayersArray();
      dirty = false;
      Object.values(state.layers)
        .filter(layer => layer.shouldShow)
        .forEach(layer => {
          const shouldShowValue = layer.shouldShow!(currentlyShowing);
          if (shouldShowValue !== layer.show) {
            layer.show = !!shouldShowValue;
            dirty = true;
          }
        });
    }
    state.layers = layers;
    renderAppRoot();
    bus.send('screen/displayHeader', { display: true });
  };

  const bus = app.bus as MessageBus;

  // Register all bus listeners synchronously — before any plugin loads
  bus.on('layers/register', args =>
    updateLayers({
      ...state.layers,
      [args.id]: { show: false, isOverlay: false, ...args },
    }),
  );
  bus.on('layers/show', ({ id }) => {
    const target = state.layers[id];
    if (!target) {
      log.warn(`Trying to show an unknown layer - ${id}`);
      return;
    }
    const newLayers = map((layer: LayerWidget) => {
      if (layer.id === id) {
        return { ...layer, show: true };
      } else if (layer.layoutId === target.layoutId && !layer.isOverlay && !target.isOverlay) {
        return { ...layer, show: false };
      } else {
        return layer;
      }
    }, state.layers);
    state.currentLayout = target.layoutName || state.currentLayout;
    updateLayers(newLayers);
  });
  bus.on('layers/hide', ({ id }) =>
    updateLayers({
      ...state.layers,
      [id]: { ...state.layers[id], show: false },
    }),
  );
  bus.on('layers/hideWithLayoutId', layoutId => {
    bus.send(
      'layers/hideMultiple',
      Object.keys(pluck('id')(filter((layer: LayerWidget) => layer.layoutId === layoutId, state.layers))),
    );
  });
  bus.on('layers/hideMultiple', ids => {
    const newLayers = { ...state.layers };
    ids.forEach((id: string) => {
      if (newLayers[id]) {
        newLayers[id].show = false;
      }
    });
    updateLayers(newLayers);
  });
  bus.on('layers/changeLayout', ({ layoutName }: { layoutName: string }) => {
    state.currentLayout = layoutName;
    renderAppRoot();
  });

  bus.on('map/mapReadyToShow', () => {
    state.mapLoaded = true;
    renderAppRoot();
  });

  bus.on('i18n/langChanged', () => renderAppRoot());

  bus.on('layers/isShowing', async ({ id }: { id: string }) => {
    const visibleLayers = filter((layer: LayerWidget) => {
      return layer.id === id && layer.show === true;
    }, state.layers);
    return Object.keys(visibleLayers).length > 0;
  });

  bus.on('layers/getLayers', () => state.layers);
  bus.on('layers/getVisibleLayersIds', () => getShowingLayersArray());
  bus.on('layers/getVisibleLayersByLayoutId', id => getVisibleLayerByLayoutId(id));
  bus.on('layers/getPreviousLayer', () => state.previousLayer);
  bus.on('layers/setPreviousLayer', id => {
    state.previousLayer = id;
    renderAppRoot();
  });
  bus.on('layers/rerender', () => renderAppRoot());
  bus.on('env/resize', () => renderAppRoot());

  bus.on('app/unmount', () => {
    appRoot!.unmount();
    appRoot = null;
  });

  // Initial render
  renderAppRoot();

  getThemeState().addCallback(renderAppRoot);
};

const destroy = async () => {
  if (appRoot) {
    appRoot.unmount();
    appRoot = null;
  }
};

export { initLayerManager, destroy };
